import { ChangeAttrName } from '@common/flare/change-attr-name.enum';
import { ToFlareXMLOptions } from '@common/flare/types/to-flare-xml-options.type';
import { FlareXMLOutputSpec } from '@common/flare/util/prosemirror-flare-xml';
import { SchemaPlugin } from '@common/prosemirror/model/schema-plugin';
import { TrackedChange } from '@common/prosemirror/tracked-changes/tracked-change.type';
import { makeTrackedChangeIdsAttrValueFromChangeList } from '@common/prosemirror/tracked-changes/tracked-changes';
import { getSchemaAttr, getSchemaAttrs, nodeToDOM } from '@common/util/schema';
import { AttributeSpec, Attrs, Mark, MarkSpec, NodeSpec, ProseMirrorNode } from 'prosemirror-model';

const changeMarkName = 'madcapchange';

function createChangeAttrs(changeList: TrackedChange[]): Attrs {
  const attrs = {};

  changeList.forEach(change => {
    // Do not include orphaned changes (changes without data, aka changes without a type)
    if (change.changeType) {
      attrs[`mc-lwe-tracked-change-${change.changeType}`] = true;
    }
  });

  // Check if any tracked change class names were added and if so add the generic tracked change class names
  // Its possible that the change list only includes orphaned changes in which case we do not want to add the tracked change class names
  if (Object.keys(attrs).length > 0) {
    attrs['mc-lwe-tracked-change'] = true;
  }

  return attrs;
}

/**
 * Builds the DOM attribute pair (`MadCap:changes` and `mc-lwe-tracked-change-ids`) for a node or mark.
 * For backwards compatibility, if `changeIds` is missing but `node.attrs.changeList` is present, the value is derived from the change list.
 * Returns an empty object when neither source produces a non-empty value, so callers can spread the result without writing empty attributes.
 * @param changeIds The current value of the `MadCap:changes` attribute.
 * @param node The node or mark whose `attrs.changeList` is consulted as a fallback.
 * @returns A dictionary with both attribute names set to the resolved value, or `{}` when there is no value.
 */
export function createChangeIdAttrs(changeIds: string, node: ProseMirrorNode | Mark): Attrs {
  // Backwards compatibility if the node has changeList but not MadCap:changes
  if (!changeIds && node.attrs.changeList) {
    changeIds = makeTrackedChangeIdsAttrValueFromChangeList(node.attrs.changeList);
  }

  if (changeIds) {
    return {
      'MadCap:changes': changeIds,
      'mc-lwe-tracked-change-ids': changeIds
    };
  } else {
    return {};
  }
}

export class MadCapChangeSchemaPlugin extends SchemaPlugin {
  marks = {
    [changeMarkName]: {
      inclusive: false,
      attrs: {
        changeList: {
          default: undefined,
          skipExport: true,
          toEditorAttr(value) {
            return value ? createChangeAttrs(value) : {};
          }
        },
        [ChangeAttrName.ChangeIds]: {
          default: undefined,
          toEditorAttr(value, mark) {
            return createChangeIdAttrs(value, mark);
          },
          toFlareXML(value: any, options: ToFlareXMLOptions, mark: Mark): string {
            return options.trackedChangesExporter.extractChanges(mark) ?? value;
          }
        }
      },
      parseDOM: [{
        tag: 'MadCap\\:change',
        preserveWhitespace: true,
        getAttrs(dom: HTMLElement): Dictionary | false {
          return getSchemaAttrs(dom, [ChangeAttrName.ChangeIds]);
        }
      }],
      toDOM(node) { return nodeToDOM(node); },
      toFlareXML(mark: Mark, options: ToFlareXMLOptions): FlareXMLOutputSpec {
        // Tracked changes are stripped when exporting for the clipboard so return nothing when that is the case
        if (!options.exportForClipboard) {
          return {
            tagName: 'MadCap:change',
            attrs: mark.attrs
          };
        }
      },
      tagName: 'MadCap:change'
    }
  } satisfies Record<string, MarkSpec>;

  props: Dictionary = {
    madcapChangeMarkName: changeMarkName
  };

  modifyNodesAndMarks(nodes: Dictionary<NodeSpec>, marks: Dictionary<MarkSpec>) {
    Object.entries(nodes).forEach(([name, nodeSpec]) => {
      if (name !== 'text') {
        // Add the default attributes for tracked changes
        nodeSpec.attrs = Object.assign<Dictionary<AttributeSpec>, Dictionary<AttributeSpec>>(nodeSpec.attrs || {}, {
          changeList: {
            default: undefined,
            skipExport: true,
            toEditorAttr(value) {
              return value ? createChangeAttrs(value) : {};
            }
          },
          [ChangeAttrName.ChangeIds]: {
            default: undefined,
            toEditorAttr(value, node) {
              return createChangeIdAttrs(value, node);
            },
            toFlareXML(value: any, options: ToFlareXMLOptions, node: ProseMirrorNode): string {
              if (!options.exportForClipboard) {
                return options.trackedChangesExporter.extractChanges(node) ?? value;
              }
            }
          }
        });
        nodeSpec.skippedTrackingAttributes = [...(nodeSpec.skippedTrackingAttributes ?? []), ChangeAttrName.ChangeIds, 'changeList'];

        // Update the node's parse rules to read in the tracked changes attributes
        if (Array.isArray(nodeSpec.parseDOM)) {
          nodeSpec.parseDOM.forEach(parser => {
            const originalGetAttrs = parser.getAttrs;

            parser.getAttrs = function (dom: Element): Dictionary | false {
              let attrs: Dictionary | false = originalGetAttrs ? originalGetAttrs.apply(this, arguments) : {};

              if (attrs === false) {
                return false;
              }

              const changeIds = getSchemaAttr(dom, ChangeAttrName.ChangeIds);
              if (changeIds) {
                attrs = attrs ?? {}; // Ensure attrs exists
                attrs[ChangeAttrName.ChangeIds] = changeIds;
              }

              return attrs;
            };
          });
        }
      }
    });
  }
}
